import { useCallback, useEffect, useState } from "react";
import { aiGetConfig, aiSelect, aiTest, aiReset } from "@/lib/rootConsole";

const card = { background: "#121924", border: "1px solid #22303f", borderRadius: 10, padding: 16, marginBottom: 16 };
const btn = { background: "#1f6feb", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", cursor: "pointer", marginRight: 8, marginTop: 8 };
const ghost = { ...btn, background: "#30363d" };
const danger = { ...btn, background: "#8957e5" };
const input = { background: "#0b0f14", color: "#d7e0ea", border: "1px solid #22303f", borderRadius: 8, padding: 10, width: "100%", marginTop: 8, fontFamily: "inherit" };
const label = { color: "#8b98a5", fontSize: 12, marginTop: 10, display: "block" };

export default function AiEnginePanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);
  const [form, setForm] = useState({ base_url: "", model: "", engine: "openai", api_key: "", allow_cloud: false });

  const load = useCallback(async () => {
    try {
      const d = await aiGetConfig();
      setData(d); setErr(null);
      setForm((f) => ({ ...f, base_url: d.active.base_url || "", model: d.active.model || "", engine: d.active.engine || "openai", allow_cloud: !!d.active.allow_cloud }));
    } catch (e) { setErr(e?.response?.data?.detail || "Connectez-vous en Founder pour gérer le moteur IA."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const doSelect = async () => {
    setBusy(true); setErr(null);
    try {
      const body = { base_url: form.base_url.trim(), model: form.model.trim(), engine: form.engine, allow_cloud: form.allow_cloud };
      if (form.api_key.trim()) body.api_key = form.api_key.trim();
      await aiSelect(body);
      setForm((f) => ({ ...f, api_key: "" }));
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || String(e)); } finally { setBusy(false); }
  };

  const doTest = async (useForm) => {
    setBusy(true); setErr(null); setTest(null);
    try {
      const body = useForm ? { base_url: form.base_url.trim(), model: form.model.trim(), engine: form.engine, allow_cloud: form.allow_cloud } : {};
      if (useForm && form.api_key.trim()) body.api_key = form.api_key.trim();
      setTest(await aiTest(body));
    } catch (e) { setErr(e?.response?.data?.detail || String(e)); } finally { setBusy(false); }
  };

  const doReset = async () => { setBusy(true); setErr(null); try { await aiReset(); setForm((f) => ({ ...f, api_key: "" })); await load(); } catch (e) { setErr(String(e)); } finally { setBusy(false); } };

  const st = data?.status;
  return (
    <div style={card} data-testid="root-ai-engine">
      <h3 style={{ color: "#58a6ff" }}>Moteur IA de Tuluss (open source · local · cloud)</h3>
      <div style={{ color: "#8b98a5", fontSize: 12, marginBottom: 8 }}>
        Choisis, teste et bascule le modèle qui alimente Tuluss. Portable hors Emergent : pointe
        simplement vers ton serveur (Ollama/vLLM/llama.cpp) ou active un moteur cloud explicitement.
      </div>
      {err && <div style={{ ...card, borderColor: "#f85149", color: "#ff7b72", marginBottom: 10 }} data-testid="ai-error">{err}</div>}

      {st && (
        <div style={{ fontSize: 13, marginBottom: 10 }} data-testid="ai-active">
          <div>Actif : <b style={{ color: st.configured ? "#7ee787" : "#ff7b72" }}>{st.model || "—"}</b> <span style={{ color: "#8b98a5" }}>({st.source})</span></div>
          <div style={{ color: "#8b98a5" }}>Endpoint : {st.base_url || "—"}</div>
          <div style={{ color: "#8b98a5" }}>
            État : <span style={{ color: st.configured ? "#7ee787" : "#e3b341" }}>{st.configured ? "CONFIGURÉ" : "NON CONFIGURÉ"}</span>
            {st.is_cloud && <span style={{ color: st.allow_cloud ? "#e3b341" : "#ff7b72", marginLeft: 8 }}>· cloud {st.allow_cloud ? "AUTORISÉ" : "BLOQUÉ"}</span>}
          </div>
        </div>
      )}

      {data?.local_models?.length > 0 && (
        <div style={{ marginBottom: 8 }} data-testid="ai-local-models">
          <span style={label}>Modèles locaux détectés (Ollama) — cliquez pour choisir :</span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
            {data.local_models.map((m) => (
              <button key={m} data-testid={`ai-local-${m}`} onClick={() => set("model", m) || set("engine", "openai")}
                style={{ ...ghost, marginTop: 0, padding: "4px 10px", fontSize: 12, background: form.model === m ? "#1f6feb" : "#30363d" }}>{m}</button>
            ))}
          </div>
        </div>
      )}

      {data?.cloud_presets?.length > 0 && (
        <div style={{ marginBottom: 8 }} data-testid="ai-cloud-presets">
          <span style={label}>Presets cloud (nécessitent « Autoriser cloud » + clé API) :</span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
            {data.cloud_presets.map((p) => (
              <button key={p.label} data-testid={`ai-preset-${p.model}`}
                onClick={() => setForm((f) => ({ ...f, base_url: p.base_url, model: p.model, engine: p.engine, allow_cloud: true }))}
                style={{ ...ghost, marginTop: 0, padding: "4px 10px", fontSize: 12 }}>{p.label}</button>
            ))}
          </div>
        </div>
      )}

      <span style={label}>Endpoint (base_url)</span>
      <input style={input} value={form.base_url} onChange={(e) => set("base_url", e.target.value)} placeholder="http://localhost:11434" data-testid="ai-input-baseurl" />
      <span style={label}>Modèle</span>
      <input style={input} value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="qwen2.5:1.5b-instruct" data-testid="ai-input-model" />
      <span style={label}>Clé API (optionnelle — pour un moteur cloud/protégé)</span>
      <input style={input} type="password" value={form.api_key} onChange={(e) => set("api_key", e.target.value)} placeholder="••••••••" data-testid="ai-input-apikey" />
      <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, color: "#e3b341", fontSize: 13, cursor: "pointer" }}>
        <input type="checkbox" checked={form.allow_cloud} onChange={(e) => set("allow_cloud", e.target.checked)} data-testid="ai-allow-cloud" />
        Autoriser un moteur cloud (désactive la garde « 100% local »)
      </label>

      <div style={{ marginTop: 4 }}>
        <button style={btn} onClick={doSelect} disabled={busy || !form.base_url.trim() || !form.model.trim()} data-testid="ai-btn-select">Appliquer ce moteur</button>
        <button style={ghost} onClick={() => doTest(true)} disabled={busy || !form.base_url.trim() || !form.model.trim()} data-testid="ai-btn-test-form">Tester ce moteur</button>
        <button style={ghost} onClick={() => doTest(false)} disabled={busy} data-testid="ai-btn-test-active">Tester l&apos;actif</button>
        <button style={danger} onClick={doReset} disabled={busy} data-testid="ai-btn-reset">Réinitialiser (ENV)</button>
      </div>

      {busy && <div style={{ color: "#8b98a5", marginTop: 10, fontSize: 13 }}>Traitement…</div>}
      {test && (
        <div style={{ ...card, marginTop: 12, marginBottom: 0 }} data-testid="ai-test-result">
          <div>Test : <b style={{ color: test.status === "OK" ? "#7ee787" : "#ff7b72" }}>{test.status}</b> · {test.latency_ms} ms · {test.model}</div>
          {test.text && <div style={{ color: "#d7e0ea", marginTop: 4 }}>Réponse : « {test.text} »</div>}
          {test.reason && <div style={{ color: "#e3b341", marginTop: 4 }}>Raison : {test.reason}{test.code ? ` (code ${test.code})` : ""}</div>}
        </div>
      )}
    </div>
  );
}
